import { Star, ExternalLink, CheckCircle2, XCircle, Target } from "lucide-react";
import { fmtMoney } from "@/lib/api";

export function HomeListingCard({ l, mode, income, onUseTarget }) {
  const r = l.requirements || {}, rv = l.reviews || {}, q = l.qualify || {};
  const rent = mode === "rent";
  return (
    <div className="bg-cream border border-sand rounded-xl p-4" data-testid={`home-listing-${l.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-serif text-lg leading-tight" data-testid={`home-listing-name-${l.id}`}>{l.name}</div>
          <div className="text-[11px] text-moss mt-0.5 capitalize">{l.type} · {l.beds ?? "—"} bd · {l.baths ?? "—"} ba{l.sqft ? ` · ${Number(l.sqft).toLocaleString()} sqft` : ""} · {l.neighborhood}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-lg text-ink" data-testid={`home-listing-price-${l.id}`}>{fmtMoney(l.price)}{rent && <span className="text-[10px] text-sage">/mo</span>}</div>
          {rv.rating && <div className="text-[11px] text-gold inline-flex items-center gap-0.5 justify-end"><Star className="w-3 h-3 fill-gold" /> {Number(rv.rating).toFixed(1)}</div>}
        </div>
      </div>
      {l.summary && <p className="text-xs text-moss mt-2">{l.summary}</p>}
      {l.live && l.agent && (l.agent.name || l.agent.phone || l.agent.website) && (
        <div className="text-[11px] text-moss mt-1 flex flex-wrap gap-x-3" data-testid={`home-listing-agent-${l.id}`}>
          {l.agent.name && <span>{l.agent.name}</span>}
          {l.agent.phone && <a href={`tel:${l.agent.phone}`} className="underline underline-offset-2 hover:text-ink">{l.agent.phone}</a>}
          {l.agent.website && <a href={l.agent.website.startsWith("http") ? l.agent.website : `https://${l.agent.website}`} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink">Agent site</a>}
        </div>
      )}

      <div className="mt-3 rounded-lg bg-linen border border-sand p-2.5 text-[11px]" data-testid={`home-listing-reqs-${l.id}`}>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-sage mb-1">To secure it</div>
        {rent ? (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-ink">
            <span><strong>{Number(r.income_multiple || 3).toString()}×</strong> rent in income</span>
            {r.credit_min && <span>credit <strong>{r.credit_min}+</strong></span>}
            <span>deposit <strong>{r.deposit_months ?? 1} mo</strong></span>
            {r.application_fee != null && <span>app fee <strong>{fmtMoney(r.application_fee)}</strong></span>}
            {r.background_check && <span>background check</span>}
          </div>
        ) : (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-ink">
            <span>down (3.5% FHA) <strong>{fmtMoney(q.down_payment)}</strong></span>
            <span>closing <strong>{fmtMoney(q.closing_costs)}</strong></span>
            <span>est. payment <strong>{fmtMoney(q.est_monthly_payment)}/mo</strong></span>
            {r.credit_min && <span>credit <strong>{r.credit_min}+</strong></span>}
          </div>
        )}
        {r.notes && <div className="text-moss mt-1">{r.notes}</div>}
        <div className="mt-1.5 flex items-center gap-1.5" data-testid={`home-listing-qualify-${l.id}`}>
          {q.qualifies === true && <><CheckCircle2 className="w-3 h-3 text-forest" /><span className="text-forest font-medium">You qualify · needs {fmtMoney(q.required_monthly_income)}/mo gross</span></>}
          {q.qualifies === false && <><XCircle className="w-3 h-3 text-clay" /><span className="text-clay font-medium">Needs {fmtMoney(q.required_monthly_income)}/mo gross · you're {fmtMoney(q.income_gap)} short</span></>}
          {q.qualifies == null && <span className="text-sage">Needs {fmtMoney(q.required_monthly_income)}/mo gross ({q.rule}) — add your income to check</span>}
        </div>
      </div>

      {(rv.pros?.length || rv.cons?.length) && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]" data-testid={`home-listing-reviews-${l.id}`}>
          <div><div className="text-forest font-semibold">Residents like</div>{(rv.pros || []).map((p, i) => <div key={i} className="text-moss">· {p}</div>)}</div>
          <div><div className="text-clay font-semibold">Watch for</div>{(rv.cons || []).map((c, i) => <div key={i} className="text-moss">· {c}</div>)}</div>
        </div>
      )}

      <div className="mt-3 flex gap-2 flex-wrap">
        <a href={l.apply_url} target="_blank" rel="noreferrer" className="flex-1 min-w-[120px] bg-ink text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep inline-flex items-center justify-center gap-1" data-testid={`home-listing-apply-${l.id}`}>
          {l.live ? (rent ? "View & apply" : "View listing") : rent ? "Find & apply" : "Find listing"} <ExternalLink className="w-3 h-3" />
        </a>
        <button onClick={() => onUseTarget(l.suggested_target)} className="flex-1 min-w-[120px] border border-gold text-gold rounded-md px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white inline-flex items-center justify-center gap-1" data-testid={`home-listing-target-${l.id}`}>
          <Target className="w-3 h-3" /> Save {fmtMoney(l.suggested_target)} goal
        </button>
      </div>
      <div className="mt-1 text-[10px] text-sage">{rent ? "Goal = deposit + first month + app fee + $500 moving buffer" : "Goal = 3.5% down + ~3% closing"}</div>
    </div>
  );
}
